// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Alert, Skeleton } from '@/components/ui'
import { Input } from '@/components/ui/Input'
import { useProperty } from '@/hooks/useProperties'
import { useFeeConfigs, useUpsertFeeConfigs } from '@/hooks/useParkingFeeConfigs'
import { useNotificationStore } from '@/stores/useNotificationStore'
import type { VehicleType } from '@/types/parking'

const VEHICLE_TYPES: VehicleType[] = ['car', 'motorcycle', 'pickup', 'bicycle', 'van']

interface FeeRow {
  vehicle_type: VehicleType
  included_spots: number
  addon_monthly_fee: number
  external_monthly_fee: number
  external_daily_fee: number
}

interface ValidationErrors {
  [key: string]: string
}

function buildDefaultRows(configs: Array<{ vehicle_type: VehicleType; included_spots: number; addon_monthly_fee: number; external_monthly_fee: number; external_daily_fee: number }> | undefined): FeeRow[] {
  return VEHICLE_TYPES.map((vt) => {
    const existing = configs?.find((c) => c.vehicle_type === vt)
    return {
      vehicle_type: vt,
      included_spots: existing?.included_spots ?? 1,
      addon_monthly_fee: existing?.addon_monthly_fee ?? 0,
      external_monthly_fee: existing?.external_monthly_fee ?? 0,
      external_daily_fee: existing?.external_daily_fee ?? 0,
    }
  })
}

function validateRows(rows: FeeRow[], t: (key: string) => string): ValidationErrors {
  const errors: ValidationErrors = {}

  rows.forEach((row) => {
    const prefix = row.vehicle_type

    if (row.included_spots < 0 || !Number.isInteger(row.included_spots)) {
      errors[`${prefix}_included_spots`] = t('validation.included_spots_non_negative')
    }
    if (row.addon_monthly_fee < 0) {
      errors[`${prefix}_addon_monthly_fee`] = t('validation.fee_non_negative')
    }
    if (row.external_monthly_fee < 0) {
      errors[`${prefix}_external_monthly_fee`] = t('validation.fee_non_negative')
    }
    if (row.external_daily_fee < 0) {
      errors[`${prefix}_external_daily_fee`] = t('validation.fee_non_negative')
    }
  })

  return errors
}

export function ParkingFeeConfigPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { propertyId } = useParams<{ propertyId: string }>()
  const propertyIdNum = Number(propertyId)

  const { data: property, isLoading: propertyLoading } = useProperty(propertyIdNum)
  const { data: configsResponse, isLoading: configsLoading } = useFeeConfigs(propertyIdNum)
  const upsertFeeConfigs = useUpsertFeeConfigs()

  const configs = configsResponse?.data

  // null means "not yet edited by user" — derive from server data
  const [localRows, setRows] = useState<FeeRow[] | null>(null)
  const rows = localRows ?? buildDefaultRows(configs)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [saveError, setSaveError] = useState('')
  const addToast = useNotificationStore((s) => s.addToast)

  const handleFieldChange = (
    vehicleType: VehicleType,
    field: keyof Omit<FeeRow, 'vehicle_type'>,
    rawValue: string
  ) => {
    const parsed = field === 'included_spots'
      ? parseInt(rawValue, 10)
      : parseFloat(rawValue)
    const value = isNaN(parsed) ? 0 : parsed

    setRows((prev) => {
      const current = prev ?? buildDefaultRows(configs)
      return current.map((row) =>
        row.vehicle_type === vehicleType ? { ...row, [field]: value } : row
      )
    })

    // Clear field-level error on change
    const errorKey = `${vehicleType}_${field}`
    if (errors[errorKey]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[errorKey]
        return next
      })
    }
  }

  const handleSave = async () => {
    setSaveError('')

    const validationErrors = validateRows(rows, t)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      await upsertFeeConfigs.mutateAsync({
        property_id: propertyIdNum,
        configs: rows,
      })
      addToast({ type: 'success', message: t('fee_configs.saved') })
    } catch {
      setSaveError(tc('errors.something_went_wrong'))
    }
  }

  // D9 (EP-147): the parking_fee_enabled module gate used to be checked here
  // (single-gate, no Unleash flag, mirroring the LPR page). It moved to the
  // route level, in the parkingFee ModuleRoute wrapping this page in App.tsx.

  const isLoading = propertyLoading || configsLoading

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-8 w-48" />
          <Card>
            <Skeleton className="h-64 w-full" />
          </Card>
        </div>
    )
  }

  return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: property?.name ?? '...', href: `/properties/${propertyId}` },
            { label: t('fee_configs.title') },
          ]}
        />

        <PageHeader
          title={t('fee_configs.title')}
          description={property?.name}
          actions={
            <Button
              size="sm"
              onClick={handleSave}
              loading={upsertFeeConfigs.isPending}
              leftIcon={<Save className="w-4 h-4" />}
            >
              {t('fee_configs.save_all')}
            </Button>
          }
        />

        {saveError && (
          <Alert variant="error" onClose={() => setSaveError('')}>
            {saveError}
          </Alert>
        )}

        <Card>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('fee_configs.vehicle_type')}</th>
                  <th>{t('fee_configs.included_spots')}</th>
                  <th>{t('fee_configs.addon_monthly_fee')}</th>
                  <th>{t('fee_configs.external_monthly_fee')}</th>
                  <th>{t('fee_configs.external_daily_fee')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.vehicle_type}>
                    {/* Vehicle Type — read-only label */}
                    <td className="font-medium whitespace-nowrap">
                      {t(`vehicles.types.${row.vehicle_type}`)}
                    </td>

                    {/* Included Spots */}
                    <td>
                      <Input
                        type="number"
                        size="sm"
                        className="w-24"
                        value={row.included_spots}
                        min={0}
                        step={1}
                        aria-label={`${t(`vehicles.types.${row.vehicle_type}`)} ${t('fee_configs.included_spots')}`}
                        aria-invalid={!!errors[`${row.vehicle_type}_included_spots`]}
                        error={errors[`${row.vehicle_type}_included_spots`]}
                        onChange={(e) =>
                          handleFieldChange(row.vehicle_type, 'included_spots', e.target.value)
                        }
                      />
                    </td>

                    {/* Addon Monthly Fee */}
                    <td>
                      <div className="form-control">
                        <Input
                          type="number"
                          size="sm"
                          className="w-32"
                          leftIcon={<span className="text-base-content/50 text-sm">฿</span>}
                          value={row.addon_monthly_fee}
                          min={0}
                          step={1}
                          aria-label={`${t(`vehicles.types.${row.vehicle_type}`)} ${t('fee_configs.addon_monthly_fee')}`}
                          aria-invalid={!!errors[`${row.vehicle_type}_addon_monthly_fee`]}
                          error={errors[`${row.vehicle_type}_addon_monthly_fee`]}
                          onChange={(e) =>
                            handleFieldChange(row.vehicle_type, 'addon_monthly_fee', e.target.value)
                          }
                        />
                      </div>
                    </td>

                    {/* External Monthly Fee */}
                    <td>
                      <div className="form-control">
                        <Input
                          type="number"
                          size="sm"
                          className="w-32"
                          leftIcon={<span className="text-base-content/50 text-sm">฿</span>}
                          value={row.external_monthly_fee}
                          min={0}
                          step={1}
                          aria-label={`${t(`vehicles.types.${row.vehicle_type}`)} ${t('fee_configs.external_monthly_fee')}`}
                          aria-invalid={!!errors[`${row.vehicle_type}_external_monthly_fee`]}
                          error={errors[`${row.vehicle_type}_external_monthly_fee`]}
                          onChange={(e) =>
                            handleFieldChange(row.vehicle_type, 'external_monthly_fee', e.target.value)
                          }
                        />
                      </div>
                    </td>

                    {/* External Daily Fee */}
                    <td>
                      <div className="form-control">
                        <Input
                          type="number"
                          size="sm"
                          className="w-32"
                          leftIcon={<span className="text-base-content/50 text-sm">฿</span>}
                          value={row.external_daily_fee}
                          min={0}
                          step={1}
                          aria-label={`${t(`vehicles.types.${row.vehicle_type}`)} ${t('fee_configs.external_daily_fee')}`}
                          aria-invalid={!!errors[`${row.vehicle_type}_external_daily_fee`]}
                          error={errors[`${row.vehicle_type}_external_daily_fee`]}
                          onChange={(e) =>
                            handleFieldChange(row.vehicle_type, 'external_daily_fee', e.target.value)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
  )
}
